import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Caption } from '../../components/Caption/Caption.js';
import { createCustomBlockSpec } from '../../routines/createCustomBlockSpec.js';
import { getMediaUrl } from '../../routines/getMediaUrl.js';
import { openFilePicker } from '../../routines/openFilePicker.js';
import { FigureWithCaption } from '@blocknote/react';
import { VolumeOutlineIcon } from '@contra/icons/icons';
import { useCallback, useEffect, useState } from 'react';
/**
 * The Audio block contains one of three properties identifying what audio to render:
 * - `uid`: The UID of the uploaded audio after it has been uploaded to Cloudinary
 * - `url`: The data URI of the audio.
 * - `urlFromHtml`: The URL of the audio . This is a remote URL referenced by the `src` attribute of an `img` tag
 *
 * If an audio is selected directly from a file, the `url` property will be set to the data URI representing the audio.
 * An upload is initiated. Once the upload completes, the `uid` property is set and the `url` property is set to an empty string.
 *
 * If an audio is added when pasting HTML, if the `data-uid` attribute is present on the `img` tag, the `uid` property
 * will be set to the value of the `data-uid` attribute. Otherwise, the `url` is set to "".
 * Once the audio is rendered in the editor, we use a `useEffect` to initiate an upload. Once the upload completes, the `uid` property is set and the `url` property is set to an empty string.
 */
const propertySchema = {
    audioCodecName: {
        default: '',
    },
    backgroundColor: {
        default: 'default',
    },
    caption: {
        default: '',
    },
    checksum: {
        default: '',
    },
    duration: {
        default: 0,
    },
    name: {
        default: '',
    },
    originalFormat: {
        default: '',
    },
    placeholderId: {
        default: '',
    },
    showPreview: {
        default: true,
    },
    sizeBytes: {
        default: 0,
    },
    textAlignment: {
        default: 'left',
        values: ['left', 'center', 'right', 'justify'],
    },
    uid: {
        default: '',
    },
    url: {
        default: '',
    },
    urlFromHtml: {
        default: '',
    },
};
const blockConfig = {
    content: 'none',
    fileBlockAccept: [
        'audio/aac',
        'audio/mp3',
        'audio/m4a',
        'audio/ogg',
        'audio/wav',
    ],
    isFileBlock: true,
    propSchema: propertySchema,
    type: 'audio',
};
const AudioElement = ({ source }) => {
    return (_jsx("audio", { className: "bn-audio", contentEditable: false, controls: true, draggable: false, preload: "metadata", src: source, style: {
            width: '100%',
        } }));
};
const EditableAudioElement = ({ block, editor, }) => {
    const [dataUri, setDataUri] = useState(null);
    const handleAddAudioClick = useCallback(() => {
        openFilePicker({ block, editor });
    }, [block, editor]);
    // `block.props.url` will be set to an empty string after the upload completes --
    // in this case, we want to continue using the data URI for rendering the audio element.
    // Afterwards, we'll use the `block.props.uid` to determine the source of the audio.
    useEffect(() => {
        if (block.props.url) {
            setDataUri(block.props.url);
        }
    }, [block.props.url]);
    // Uploads audio for audio elements that were pasted into the editor from the clipboard
    useEffect(() => {
        if (block.props.urlFromHtml && !block.props.uid) {
            const upload = editor.uploadFile;
            upload?.(block.props.urlFromHtml)
                // eslint-disable-next-line promise/prefer-await-to-then
                .then(async (payload) => {
                if (typeof payload !== 'string') {
                    editor.updateBlock(block, {
                        props: payload.props,
                    });
                }
            });
        }
    }, [block.props.urlFromHtml, block.props.uid]);
    // This is an optimization to avoid long initial load times when we finally render the published content.
    // When the uid changes, we trigger loading the audio.
    // This way, even if we use the data URI while editing, we can still quickly load the audio
    // once the content is actually published
    useEffect(() => {
        if (block.props.uid) {
            const audioElement = document.createElement('audio');
            audioElement.src = getMediaUrl({
                assetType: 'video',
                format: 'aac',
                uid: block.props.uid,
            });
        }
    }, [block.props.uid]);
    return Boolean(block.props.uid) || Boolean(block.props.url) ? (_jsx("div", { className: "bn-file-block-content-wrapper", children: _jsxs("div", { className: "contra-bn-image-and-caption-wrapper", children: [_jsx(AudioElement, { source: dataUri ??
                        getMediaUrl({
                            assetType: 'video',
                            format: 'aac',
                            uid: block.props.uid,
                        }) }), _jsx(Caption, { block: block, editor: editor, onChange: (value) => {
                        editor.updateBlock(block.id, { props: { caption: value } });
                    } })] }) })) : (_jsxs("button", { className: "contra-bn-image-placeholder", onClick: handleAddAudioClick, type: "button", children: [_jsx(VolumeOutlineIcon, { color: "#677084", size: 24 }), "Add audio"] }));
};
const parseAudioElement = (audioElement) => {
    const urlFromHtml = audioElement.src || audioElement.querySelectorAll('source')[0]?.src || '';
    const uid = audioElement.dataset.uid || '';
    if (!urlFromHtml && !uid) {
        return undefined;
    }
    return {
        uid,
        url: '',
        urlFromHtml: uid ? '' : urlFromHtml,
    };
};
const parseFigureElement = (figureElement, targetTag) => {
    const targetElement = figureElement.querySelector(targetTag);
    if (!targetElement) {
        return undefined;
    }
    const captionElement = figureElement.querySelector('figcaption');
    const caption = captionElement?.textContent ?? undefined;
    return { caption, targetElement };
};
export const Audio = createCustomBlockSpec(blockConfig, {
    parse: (element) => {
        if (element.tagName === 'AUDIO') {
            return parseAudioElement(element);
        }
        if (element.tagName === 'FIGURE') {
            const parsedFigure = parseFigureElement(element, 'audio');
            if (!parsedFigure) {
                return undefined;
            }
            const { caption, targetElement } = parsedFigure;
            const audioElement = parseAudioElement(targetElement);
            if (!audioElement) {
                return undefined;
            }
            return {
                ...audioElement,
                caption,
            };
        }
        return undefined;
    },
    render: (props) => {
        if (props.readOnly) {
            const { block } = props;
            return (_jsx("div", { className: "bn-file-block-content-wrapper", children: _jsxs("div", { className: "contra-bn-image-and-caption-wrapper", children: [_jsx(AudioElement, { source: getMediaUrl({
                                assetType: 'video',
                                format: 'aac',
                                uid: block.props.uid,
                            }) }), _jsx(Caption, { block: props.block })] }) }));
        }
        else {
            const { block, editor } = props;
            return (_jsx(EditableAudioElement, { block: block, editor: editor }));
        }
    },
    toExternalHTML: ({ block }) => {
        if (!block.props.uid) {
            return null;
        }
        const audio = (_jsx("audio", { "data-uid": block.props.uid, src: getMediaUrl({
                assetType: 'video',
                format: 'aac',
                uid: block.props.uid,
            }) }));
        if (block.props.caption) {
            return (_jsx(FigureWithCaption, { caption: block.props.caption, children: audio }));
        }
        return audio;
    },
});
//# sourceMappingURL=Audio.js.map